import * as ko from "knockout";
import {_} from 'lodash';
import {global, lm} from "../../../appContext";
import pubsub from "../../../pubsub";
import {ICloneDialogOptions} from "../../../clone/index";
import {IExportTemplate, IRuleset, ISearchParams} from "../../../index";
import {MAX_RESULTS_PER_PAGE} from "../../../constants";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../../constants/Language";
import {getHash} from "../../../constants/RouteDefinitions";
import {createErrorMessage, createErrorString} from "../../../util/misc";
import {nsClone} from "../../../clone/fn";
import targetOrg = nsClone.targetOrg;
import {
    copyRulesetsWithApis,
    ICopyRulesetResult,
    topologicalCopyOrder,
} from "../../ruleset/copy-wizard/copyRulesetGraph";
import {RulesetNameConflictRowVm} from "../../ruleset/copy-wizard/rulesetNameConflictRow";
import type {IStepperStep} from "../../ruleset/copy-wizard/sub/stepper-host";
import {nsSynchronizationTemplateClone} from "../../../clone/nsSynchronizationTemplateClone";
import {
    buildOrderedPreviewRows,
    collectRulesetClosureForSyncTemplates,
    ICopyPreviewRow,
} from "./copySyncTemplatePlan";
import {isRulesetNameValidForCopyWizard} from "../../ruleset/copy-wizard/copyWizardStrictNames";
import {SyncTemplateNameConflictRowVm} from "./syncTemplateNameConflictRow";
import mainHtml from './main.html?raw';

export const template = mainHtml;

const PREFIX = "synctemplate.copy.wizard";

export enum WizardInternalStep {
    LoadSource = 1,
    PickTargetOrg = 2,
    LoadTarget = 3,
    ResolveConflicts = 4,
    Process = 5,
    Receipt = 6,
}

export interface ISyncTemplateCopyWizardParams {
    dialog: any;
    item?: IExportTemplate;
    selectedObjects?: IExportTemplate[];
    options?: ICloneDialogOptions;
    requestParams?: ISearchParams;
}

export type ISyncTemplateCopyRow =
    | {kind: "synctemplate"; vm: SyncTemplateNameConflictRowVm}
    | {kind: "ruleset"; vm: RulesetNameConflictRowVm};

export type ICopySyncTemplateApiResult =
    | {sourceId: number; success: true; targetTemplate: IExportTemplate}
    | {sourceId: number; success: false; error: string};

export class viewModel {
    readonly internalStep = ko.observable<WizardInternalStep>(WizardInternalStep.LoadSource);
    readonly messageData = ko.observable(null);
    readonly headingKey = ko.observable(`${PREFIX}.loadsource`);
    readonly descriptionKey = ko.observable(`${PREFIX}.loadsource.description`);
    readonly showStepper = ko.observable(false);
    readonly stepperCurrentIndex = ko.observable(0);
    readonly stepperSteps: ReadonlyArray<IStepperStep> = [
        {
            labelKey: `${PREFIX}.stepper.picktargetorg`,
            descriptionKey: `${PREFIX}.stepper.picktargetorg.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.resolveconflicts`,
            descriptionKey: `${PREFIX}.stepper.resolveconflicts.description`,
        },
        {
            labelKey: `${PREFIX}.stepper.receipt`,
            descriptionKey: `${PREFIX}.stepper.receipt.description`,
        },
    ];

    readonly srcOrg = global.login.selectedOrganization;
    readonly organizations = global.login.organizations;
    readonly trgOrg = targetOrg;

    readonly rootTemplatesOrdered = ko.observableArray<IExportTemplate>([]);
    /** When false, template-level rulesets (and sub-rulesets) are not copied; mapping/export-flow rulesets remain. */
    readonly copyMainRulesets = ko.observable(true);
    readonly uniqueRulesets = ko.observableArray<IRuleset>([]);
    readonly orderedPreviewRows = ko.observableArray<ICopyPreviewRow>([]);
    readonly combinedConflictRows = ko.observableArray<ISyncTemplateCopyRow>([]);
    readonly rulesetCopyResults = ko.observableArray<ICopyRulesetResult>([]);
    readonly templateCopyResults = ko.observableArray<ICopySyncTemplateApiResult>([]);

    readonly showPrevious = ko.observable(false);
    readonly showNext = ko.observable(false);
    readonly showCancel = ko.observable(true);
    readonly canGoPrevious = ko.pureComputed(
        () => this.internalStep() === WizardInternalStep.ResolveConflicts,
    );
    readonly canGoNext = ko.pureComputed((): boolean => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                return !!this.trgOrg()?.organizationid;
            case WizardInternalStep.ResolveConflicts:
                return this.canAdvanceFromConflictStep();
            default:
                return false;
        }
    });
    readonly nextLabelKey = ko.observable("next");
    readonly nextButtonClassMap = ko.observable<Record<string, boolean>>({
        "btn-primary": true,
        "btn-success": false,
    });
    readonly cancelLabelKey = ko.observable("cancel");
    readonly cancelIcon = ko.observable("fa-ban");

    private sourceTemplatesById = new Map<number, IExportTemplate>();
    private sourceRulesetsById = new Map<number, IRuleset>();
    private targetRulesetsList: IRuleset[] = [];
    private targetTemplatesList: IExportTemplate[] = [];
    private activeSearchRequest: ReturnType<typeof global.getPromiseRequest> | null = null;

    constructor(private readonly params: ISyncTemplateCopyWizardParams) {
        if (!this.trgOrg() && this.organizations().length) {
            const srcOrg = this.srcOrg();
            if (srcOrg) {
                this.trgOrg(srcOrg);
            }
        }
        this.internalStep.subscribe((s) => this.onStepChanged(s));
        this.copyMainRulesets.subscribe(() => this.rebuildSyncTemplateRulesetPlan());
        this.onStepChanged(this.internalStep());
    }

    readonly canAdvanceFromConflictStep = ko.pureComputed((): boolean => {
        if (this.internalStep() !== WizardInternalStep.ResolveConflicts) {
            return false;
        }
        this.combinedConflictRows().forEach((r) => {
            r.vm.draftName();
        });
        return this.combinedConflictRows().every((r) => !r.vm.hasBlockingConflict());
    });

    private onStepChanged(step: WizardInternalStep) {
        this.updateChrome(step);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.runLoadSource();
                break;
            case WizardInternalStep.PickTargetOrg:
                break;
            case WizardInternalStep.LoadTarget:
                this.runLoadTarget();
                break;
            case WizardInternalStep.ResolveConflicts:
                this.prepareConflictRows();
                break;
            case WizardInternalStep.Process:
                this.runProcess();
                break;
            case WizardInternalStep.Receipt:
                break;
            default:
                break;
        }
    }

    private updateChrome(step: WizardInternalStep) {
        const keyOf = (k: string) => `${PREFIX}.${k}`;
        const showPnNav = step !== WizardInternalStep.Receipt;
        this.showPrevious(showPnNav);
        this.showNext(showPnNav);
        switch (step) {
            case WizardInternalStep.LoadSource:
                this.headingKey(keyOf("loadsource"));
                this.descriptionKey(keyOf("loadsource.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.PickTargetOrg:
                this.headingKey(keyOf("itemslist"));
                this.descriptionKey(keyOf("itemslist.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(0);
                this.showCancel(true);
                this.nextLabelKey("next");
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.LoadTarget:
                this.headingKey(keyOf("loadtarget"));
                this.descriptionKey(keyOf("loadtarget.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.ResolveConflicts:
                this.headingKey(keyOf("resolveconflicts"));
                this.descriptionKey(keyOf("resolveconflicts.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(1);
                this.showCancel(true);
                this.nextLabelKey(keyOf("submit"));
                this.nextButtonClassMap({"btn-primary": false, "btn-success": true});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Process:
                this.headingKey(keyOf("process"));
                this.descriptionKey(keyOf("process.description"));
                this.showStepper(false);
                this.showCancel(true);
                this.nextButtonClassMap({"btn-primary": true, "btn-success": false});
                this.cancelLabelKey("cancel");
                this.cancelIcon("fa-ban");
                break;
            case WizardInternalStep.Receipt:
                this.headingKey(keyOf("receipt"));
                this.descriptionKey(keyOf("receipt.description"));
                this.showStepper(true);
                this.stepperCurrentIndex(2);
                this.showCancel(true);
                this.cancelLabelKey("close");
                this.cancelIcon("fa-times");
                break;
            default:
                break;
        }
    }

    private rebuildSyncTemplateRulesetPlan() {
        const roots = this.rootTemplatesOrdered();
        if (!roots.length) {
            return;
        }
        const includeMain = this.copyMainRulesets();
        const unique = collectRulesetClosureForSyncTemplates(roots, this.sourceRulesetsById, {
            includeTemplateMainRuleset: includeMain,
        });
        this.uniqueRulesets(unique);
        this.orderedPreviewRows(buildOrderedPreviewRows(roots, unique, includeMain));
        if (this.internalStep() === WizardInternalStep.ResolveConflicts) {
            this.prepareConflictRows();
        }
    }

    private getRootTemplates(): IExportTemplate[] {
        if (this.params.selectedObjects?.length) {
            return this.params.selectedObjects;
        }
        if (this.params.item) {
            return [this.params.item];
        }
        return [];
    }

    private loadFullTemplates(roots: IExportTemplate[], orgnr: string): Promise<IExportTemplate[]> {
        const req = global.getPromiseRequest("getexporttemplate");
        return Promise.all(
            roots.map((r) =>
                req.request({id: r.id, orgnr}).then((data: IExportTemplate[]) => data[0]),
            ),
        ).then((templates) => templates.filter((t): t is IExportTemplate => t !== undefined));
    }

    private keepFullSourceRuleset(ruleset: IRuleset): IRuleset {
        const known = this.sourceRulesetsById.get(ruleset.id);
        if (known && (Array.isArray(known.rules) || !Array.isArray(ruleset.rules))) {
            return known;
        }
        this.sourceRulesetsById.set(ruleset.id, ruleset);
        return ruleset;
    }

    private runLoadSource() {
        this.setDialogLocked(false);
        this.messageData({message: REQUEST_MESSAGE});
        const srcOrg = this.srcOrg();
        if (!srcOrg) {
            return;
        }
        const orgnr = srcOrg.organizationid;
        const reqTpl = global.getPromiseRequest("searchexporttemplate");
        const reqRs = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = reqTpl;
        Promise.all([
            reqTpl.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
            reqRs.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
        ])
            .then(([templates, rulesets]: [IExportTemplate[], IRuleset[]]) => {
                this.sourceTemplatesById = new Map(templates.map((t) => [t.id, t]));
                this.sourceRulesetsById = new Map(rulesets.map((r) => [r.id, r]));
                const roots = this.getRootTemplates()
                    .map((t) => this.sourceTemplatesById.get(t.id) || t)
                    .filter(Boolean) as IExportTemplate[];
                return this.loadFullTemplates(roots, orgnr);
            })
            .then((fullRoots: IExportTemplate[]) => {
                for (const tpl of fullRoots) {
                    this.sourceTemplatesById.set(tpl.id, tpl);
                    if (tpl.ruleset) {
                        tpl.ruleset = this.keepFullSourceRuleset(tpl.ruleset);
                    }
                    for (const flow of tpl.exportflow || []) {
                        if (flow.ruleset) {
                            flow.ruleset = this.keepFullSourceRuleset(flow.ruleset);
                        }
                    }
                }
                this.rootTemplatesOrdered(fullRoots);
                this.rebuildSyncTemplateRulesetPlan();
                this.messageData(null);
                this.internalStep(WizardInternalStep.PickTargetOrg);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private runLoadTarget() {
        this.messageData({message: REQUEST_MESSAGE});
        const orgnr = this.trgOrg()?.organizationid;
        if (!orgnr) {
            this.messageData(createErrorMessage("missing organization"));
            return;
        }
        const reqTpl = global.getPromiseRequest("searchexporttemplate");
        const reqRs = global.getPromiseRequest("searchruleset");
        this.activeSearchRequest = reqTpl;
        Promise.all([
            reqTpl.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
            reqRs.request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr,
                sortcol: "name",
                asc: true,
            }),
        ])
            .then(([tplData, rsData]: [IExportTemplate[], IRuleset[]]) => {
                this.targetTemplatesList = tplData;
                this.targetRulesetsList = rsData;
                this.messageData(null);
                this.internalStep(WizardInternalStep.ResolveConflicts);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    private captureSyncTemplateConflictDraftNames(): {
        synctemplate: Map<number, string>;
        ruleset: Map<number, string>;
    } {
        const synctemplate = new Map<number, string>();
        const ruleset = new Map<number, string>();
        for (const row of this.combinedConflictRows()) {
            if (row.kind === "synctemplate") {
                synctemplate.set(row.vm.template.id, row.vm.draftName());
            } else {
                ruleset.set(row.vm.ruleset.id, row.vm.draftName());
            }
        }
        return {synctemplate, ruleset};
    }

    private prepareConflictRows() {
        const preservedDraftNames = this.captureSyncTemplateConflictDraftNames();
        const previewTemplate = this.orderedPreviewRows();
        const roots = this.rootTemplatesOrdered();
        const unique = this.uniqueRulesets();

        const templateNameCounts = new Map<string, number>();
        for (const t of roots) {
            const key = t.name.trim();
            templateNameCounts.set(key, (templateNameCounts.get(key) ?? 0) + 1);
        }
        const rulesetNameCounts = new Map<string, number>();
        for (const r of unique) {
            const key = r.name.trim();
            rulesetNameCounts.set(key, (rulesetNameCounts.get(key) ?? 0) + 1);
        }

        const built: ISyncTemplateCopyRow[] = [];
        for (const row of previewTemplate) {
            if (row.kind === "synctemplate" && row.template) {
                const t = row.template;
                const batchDup = (templateNameCounts.get(t.name.trim()) ?? 0) > 1;
                const templateVm = new SyncTemplateNameConflictRowVm(
                    t,
                    () => this.targetTemplatesList,
                    () =>
                        built
                            .filter(
                                (
                                    r,
                                ): r is {
                                    kind: "synctemplate";
                                    vm: SyncTemplateNameConflictRowVm;
                                } => r.kind === "synctemplate" && r.vm.template.id !== t.id,
                            )
                            .map((r) => r.vm.draftName().trim()),
                    batchDup,
                );
                const preservedTemplateName = preservedDraftNames.synctemplate.get(t.id);
                if (preservedTemplateName !== undefined) {
                    templateVm.draftName(preservedTemplateName);
                }
                built.push({kind: "synctemplate", vm: templateVm});
            } else if (row.kind === "ruleset" && row.ruleset) {
                const rs = row.ruleset;
                const batchDup = (rulesetNameCounts.get(rs.name.trim()) ?? 0) > 1;
                const rulesetVm = new RulesetNameConflictRowVm(
                    rs,
                    () => this.targetRulesetsList,
                    () =>
                        built
                            .filter(
                                (r): r is {kind: "ruleset"; vm: RulesetNameConflictRowVm} =>
                                    r.kind === "ruleset" && r.vm.ruleset.id !== rs.id,
                            )
                            .map((r) => r.vm.draftName().trim()),
                    batchDup,
                    isRulesetNameValidForCopyWizard,
                );
                const preservedRulesetName = preservedDraftNames.ruleset.get(rs.id);
                if (preservedRulesetName !== undefined) {
                    rulesetVm.draftName(preservedRulesetName);
                }
                built.push({kind: "ruleset", vm: rulesetVm});
            }
        }
        this.combinedConflictRows(built);
    }

    private runProcess() {
        this.setDialogLocked(true);
        this.messageData({message: UPDATING_MESSAGE});
        const trgOrgnr = this.trgOrg().organizationid;
        const rulesetNameMap = new Map<number, string>();
        const templateNameMap = new Map<number, string>();
        for (const row of this.combinedConflictRows()) {
            if (row.kind === "ruleset") {
                rulesetNameMap.set(row.vm.ruleset.id, row.vm.draftName().trim());
            } else {
                templateNameMap.set(row.vm.template.id, row.vm.draftName().trim());
            }
        }

        const ordered = topologicalCopyOrder(this.uniqueRulesets());
        copyRulesetsWithApis(ordered, trgOrgnr, rulesetNameMap)
            .then((rsResults) => {
                this.rulesetCopyResults(rsResults);
                const rulesetSourceToTarget = new Map<number, IRuleset>();
                for (const r of rsResults) {
                    if (r.success) {
                        rulesetSourceToTarget.set(r.sourceId, r.targetRuleset);
                    }
                }
                const tplOut: ICopySyncTemplateApiResult[] = [];
                const templates = this.rootTemplatesOrdered();
                const runNext = (idx: number): Promise<void> => {
                    if (idx >= templates.length) {
                        return Promise.resolve();
                    }
                    const srcRoot = templates[idx];
                    if (!srcRoot) {
                        return Promise.resolve();
                    }
                    const srcTpl = this.sourceTemplatesById.get(srcRoot.id) || srcRoot;
                    const chosenName = templateNameMap.get(srcRoot.id) ?? srcTpl.name;

                    const missingRulesetIds: number[] = [];
                    if (
                        this.copyMainRulesets() &&
                        srcTpl.ruleset &&
                        !rulesetSourceToTarget.has(srcTpl.ruleset.id)
                    ) {
                        missingRulesetIds.push(srcTpl.ruleset.id);
                    }
                    for (const flow of srcTpl.exportflow || []) {
                        if (flow.ruleset && !rulesetSourceToTarget.has(flow.ruleset.id)) {
                            missingRulesetIds.push(flow.ruleset.id);
                        }
                    }
                    if (missingRulesetIds.length) {
                        const firstMissing = missingRulesetIds[0];
                        const rsFailed = rsResults.find(
                            (x): x is Extract<ICopyRulesetResult, {success: false}> =>
                                !x.success && x.sourceId === firstMissing,
                        );
                        const errText = rsFailed
                            ? rsFailed.error
                            : lm.t("synctemplate.copy.wizard.error.rulesetmissing") ?? '';
                        tplOut.push({sourceId: srcRoot.id, success: false, error: errText});
                        return runNext(idx + 1);
                    }

                    let srcForPersist = srcTpl;
                    if (!this.copyMainRulesets() && srcTpl.ruleset) {
                        srcForPersist = _.cloneDeep(srcTpl);
                        delete (srcForPersist as {ruleset?: IRuleset}).ruleset;
                    }
                    return nsSynchronizationTemplateClone
                        .persistPreparedCopy(srcForPersist, trgOrgnr, rulesetSourceToTarget, {name: chosenName})
                        .then((created) => {
                            tplOut.push({sourceId: srcRoot.id, success: true, targetTemplate: created});
                        })
                        .catch((reason: string) => {
                            tplOut.push({
                                sourceId: srcRoot.id,
                                success: false,
                                error: createErrorString(reason, "_blank"),
                            });
                        })
                        .then(() => runNext(idx + 1));
                };
                return runNext(0).then(() => tplOut);
            })
            .then((tplResults: ICopySyncTemplateApiResult[]) => {
                this.templateCopyResults(tplResults);
                global.cacheManager.removeCache("getruleset");
                global.cacheManager.removeCache("searchruleset");
                global.cacheManager.removeCache("searchexporttemplate");
                global.cacheManager.removeCache("getexporttemplate");
                if (tplResults.some((r) => r.success)) {
                    pubsub.trigger("exporttemplate.copy.success");
                }
                if (this.rulesetCopyResults().some((r) => r.success)) {
                    pubsub.trigger("ruleset.copy.success");
                }
                const srcOrg = this.srcOrg();
                if (
                    srcOrg &&
                    srcOrg.organizationid === trgOrgnr &&
                    (tplResults.some((r) => r.success) ||
                        this.rulesetCopyResults().some((r) => r.success))
                ) {
                    pubsub.trigger("search.needs.refresh");
                }
                this.messageData(null);
                this.setDialogLocked(false);
                this.internalStep(WizardInternalStep.Receipt);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.setDialogLocked(false);
            });
    }

    private setDialogLocked(locked: boolean) {
        const d = this.params.dialog;
        if (d?.closeable && ko.isObservable(d.closeable)) {
            d.closeable(!locked);
        }
        if (d?.isClosable && ko.isObservable(d.isClosable)) {
            d.isClosable(!locked);
        }
    }

    rulesetHref = (rs: IRuleset): string =>
        getHash("rulesetdetails", {id: rs.id, orgnr: rs.companyid}) ?? '';

    templateHref = (t: IExportTemplate, orgnr: string): string =>
        getHash("synctemplatedetails", {id: t.id, orgnr}) ?? '';

    onPreviousClick = () => {
        if (this.internalStep() === WizardInternalStep.ResolveConflicts) {
            this.internalStep(WizardInternalStep.PickTargetOrg);
        }
    };

    onNextClick = () => {
        switch (this.internalStep()) {
            case WizardInternalStep.PickTargetOrg:
                this.internalStep(WizardInternalStep.LoadTarget);
                break;
            case WizardInternalStep.ResolveConflicts:
                this.internalStep(WizardInternalStep.Process);
                break;
            default:
                break;
        }
    };

    onCancelClick = () => {
        this.closeDialog();
    };

    private closeDialog() {
        const d = this.params.dialog;
        if (typeof d?.doClose === "function") {
            d.doClose();
        } else {
            pubsub.trigger("close.dialog", d);
        }
    }

    rulesetResultForSourceId(id: number): ICopyRulesetResult | undefined {
        return this.rulesetCopyResults().find((r) => r.sourceId === id);
    }

    templateResultForSourceId(id: number): ICopySyncTemplateApiResult | undefined {
        return this.templateCopyResults().find((r) => r.sourceId === id);
    }

    readonly receiptRows = ko.pureComputed(() => {
        const rows: Array<{
            preview: ICopyPreviewRow;
            rulesetResult?: ICopyRulesetResult;
            templateResult?: ICopySyncTemplateApiResult;
        }> = [];
        for (const p of this.orderedPreviewRows()) {
            if (p.kind === "synctemplate" && p.template) {
                const templateResult = this.templateResultForSourceId(p.template.id);
                if (templateResult) {
                    rows.push({preview: p, templateResult});
                }
            } else if (p.kind === "ruleset" && p.ruleset) {
                const rulesetResult = this.rulesetResultForSourceId(p.ruleset.id);
                if (rulesetResult) {
                    rows.push({preview: p, rulesetResult});
                }
            }
        }
        return rows;
    });

    readonly receiptErrorCount = ko.pureComputed(() => {
        const rs = this.rulesetCopyResults().filter((r) => !r.success).length;
        const tpl = this.templateCopyResults().filter((r) => !r.success).length;
        return rs + tpl;
    });

    readonly receiptHasErrors = ko.pureComputed(() => this.receiptErrorCount() > 0);

    receiptBannerKey = ko.pureComputed(() =>
        this.receiptHasErrors() ? `${PREFIX}.receipt.errorsoccured|n:${this.receiptErrorCount()}` : null,
    );

    dispose() {
        this.activeSearchRequest?.abort();
    }
}
